/** Parffragrance quiz v5: results, cart actions and initialisation. */
(() => {
  'use strict';
  const Q = window.ParfQuizV5;
  if (!Q?.MatchFinder) return;
  const { QUESTIONS, INTENSE_TERMS } = Q;

  Q.Finder = class ParfScentFinder extends Q.MatchFinder {
    renderProductCard(product, index) {
      const notes = product.notes || { high: [], middle: [], base: [] };
      const topFamilies = Object.entries(product.familyScores || {})
        .filter(([, score]) => score > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([family]) => family);

      const productUrl = `/products/${encodeURIComponent(product.handle)}${product.variantId ? `?variant=${encodeURIComponent(product.variantId)}` : ''}`;
      const price = this.showPrices && product.price > 0
        ? `<span class="quiz-product__price">Starting from ${this.escapeHtml(this.formatMoney(product.price))}</span>`
        : '';
      const image = product.image
        ? `<img src="${this.escapeAttribute(product.image)}" alt="${this.escapeAttribute(product.title)}" class="quiz-product__image" loading="lazy">`
        : '<div class="quiz-no-image">Image unavailable</div>';

      const noteMarkup = [
        ['Top', notes.high],
        ['Heart', notes.middle],
        ['Base', notes.base]
      ]
        .filter(([, values]) => values?.length)
        .map(([label, values]) => `<div class="quiz-note-level"><strong>${label}:</strong> ${this.escapeHtml(values.slice(0, 4).join(', '))}</div>`)
        .join('');

      return `<article class="quiz-product" role="listitem" style="--animation-order:${index}"><a class="quiz-product__image-wrapper" href="${this.escapeAttribute(productUrl)}" aria-label="View ${this.escapeAttribute(product.title)}">${image}${product.mainScent ? `<span class="quiz-main-scent">${this.escapeHtml(product.mainScent)}</span>` : ''}</a><div class="quiz-product__info"><div class="quiz-product__badges"><span class="quiz-match__badge">${product.matchScore}% match</span><span class="quiz-range-badge">${product.intensity === 'intense' ? 'Intense' : 'Regular'}</span>${product.collection === 'private' ? '<span class="quiz-range-badge quiz-range-badge--private">Private Collection</span>' : ''}</div><h3 class="quiz-product__title"><a class="quiz-product__link" href="${this.escapeAttribute(productUrl)}">${this.escapeHtml(product.title)}</a></h3><div class="quiz-product__meta"><span>${this.escapeHtml(this.prettyGender(product.gender))}</span>${product.variantTitle ? `<span>${this.escapeHtml(product.variantTitle)}</span>` : ''}</div>${price}${topFamilies.length ? `<div class="quiz-scent-tags">${topFamilies.map((family) => `<span class="quiz-scent-tag">${this.escapeHtml(this.prettyFamily(family))}</span>`).join('')}</div>` : ''}${noteMarkup ? `<div class="quiz-notes-pyramid">${noteMarkup}</div>` : ''}<ul class="quiz-match__reasons">${product.matchReasons.map((reason) => `<li>✓ ${this.escapeHtml(reason)}</li>`).join('')}</ul><div class="quiz-product__actions"><a class="quiz-btn quiz-btn--secondary quiz-product__cta" href="${this.escapeAttribute(productUrl)}">View product details</a>${product.variantId ? `<button type="button" class="quiz-btn quiz-btn--primary quiz-product__quick-add" data-variant-id="${this.escapeAttribute(product.variantId)}" data-default-label="Add to cart">Add to cart</button>` : ''}<p class="quiz-product__cart-status" aria-live="polite"></p></div></div></article>`;
    }

    async quickAdd(button) {
      const variantId = String(button.dataset.variantId || '');
      if (!variantId || button.disabled) return;

      const defaultLabel = button.dataset.defaultLabel || 'Add to cart';
      const status = button.parentElement?.querySelector('.quiz-product__cart-status');
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      button.disabled = true;
      button.classList.add('is-loading');
      button.textContent = 'Adding…';
      if (status) status.textContent = '';

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        if (cart?.getSectionsToRender) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement?.(button);
        }

        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const payload = await response.json();
        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || 'This fragrance could not be added.');
        }

        if (cart?.renderContents && payload.sections) cart.renderContents(payload);

        button.classList.remove('is-loading');
        button.classList.add('is-added');
        button.textContent = 'Added to cart';

        const productName = button.closest('.quiz-product')
          ?.querySelector('.quiz-product__title')
          ?.textContent
          ?.trim() || 'Your fragrance';

        if (status) status.textContent = `${productName} was added to your cart.`;

        window.setTimeout(() => {
          button.disabled = false;
          button.classList.remove('is-added');
          button.textContent = defaultLabel;
        }, 2600);
      } catch (error) {
        console.error('[Parffragrance Scent Finder] Quick add failed:', error);
        button.disabled = false;
        button.classList.remove('is-loading');
        button.textContent = defaultLabel;
        if (status) {
          status.textContent = error.message || 'Unable to add this fragrance. Open the product details and try again.';
        }
      }
    }

    setLoadState(state) {
      if (!this.elements.start || !this.elements.status) return;

      this.elements.start.disabled = false;
      this.elements.start.textContent = 'Start the quiz';
      this.elements.status.classList.toggle('quiz-load-status--error', state === 'error');

      if (state === 'ready') {
        this.elements.status.textContent = 'Your fragrance catalogue is ready.';
      } else if (state === 'error') {
        this.elements.status.textContent = 'We will try loading your recommendations again when you finish the quiz.';
      } else {
        this.elements.status.textContent = 'Preparing your fragrance matches in the background…';
      }
    }

    setActiveState(name) {
      Object.entries(this.states).forEach(([stateName, element]) => {
        const active = stateName === name;
        element.classList.toggle('active', active);
        element.setAttribute('aria-hidden', String(!active));
      });
    }

    scrollToQuiz() {
      const top = this.container.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({
        top,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    preferCandidate(candidate, existing) {
      if (candidate.available !== existing.available) return candidate.available;
      if (candidate.price !== existing.price) return candidate.price < existing.price;
      return candidate.variantTitle.length < existing.variantTitle.length;
    }

    canonicalScentKey(title, handle) {
      return `${title || ''} ${handle || ''}`
        .toLowerCase()
        .replace(/\b(in\s*line\s*of|inspired\s*by|private\s*collection|perfume|parfum|fragrance)\b/g, ' ')
        .replace(INTENSE_TERMS, ' ')
        .replace(/\b(regular|edp|edt|eau\s*de\s*parfum|eau\s*de\s*toilette|for\s*(men|women|him|her)|men|women|unisex)\b/g, ' ')
        .replace(/\b\d+\s*ml\b/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    cleanVariantTitle(title) {
      return String(title || '')
        .replace(/default title/i, '')
        .replace(/\s*\/\s*/g, ' · ')
        .trim();
    }

    normaliseTags(tags) {
      if (Array.isArray(tags)) return tags.map(String);
      return String(tags || '')
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }

    splitNotes(value) {
      return String(value || '')
        .split(/[,;|]+|\s+and\s+/i)
        .map((note) => note.trim().replace(/[.]+$/, ''))
        .filter((note) => note.length > 1)
        .slice(0, 20);
    }

    htmlToText(html) {
      const parsed = new DOMParser().parseFromString(String(html || ''), 'text/html');
      parsed.querySelectorAll('br').forEach((node) => node.replaceWith(' '));
      return parsed.body.textContent || '';
    }

    labelForValue(questionId, value) {
      return QUESTIONS
        .find((question) => question.id === questionId)
        ?.options.find((option) => option.value === value)
        ?.label || value;
    }

    prettyFamily(family) {
      return {
        citrus: 'Citrus',
        fresh: 'Fresh',
        floral: 'Floral',
        woody: 'Woody',
        amber: 'Amber',
        spicy: 'Spicy',
        oud: 'Oud',
        leather: 'Leather',
        sweet: 'Sweet',
        musk: 'Musky'
      }[family] || family;
    }

    prettyGender(gender) {
      return {
        men: 'For him',
        women: 'For her',
        unisex: 'Unisex'
      }[gender] || 'Unisex';
    }

    formatMoney(amount) {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-AE', {
          style: 'currency',
          currency: this.currency,
          maximumFractionDigits: 2
        }).format(amount);
      } catch (_error) {
        return `${amount.toFixed(2)} ${this.currency}`;
      }
    }

    escapeRegExp(value) {
      return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    escapeHtml(value) {
      return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#39;',
        '"': '&quot;'
      }[character]));
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/`/g, '&#96;');
    }
  };

  const initialise = (root = document) => {
    root.querySelectorAll('.perfume-quiz').forEach((container) => new Q.Finder(container));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
